import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { auctionsApi } from "../../api/auctionsApi";
import { auctionPropertiesApi } from "../../api/auctionPropertiesApi";

function isFutureOrNoDate(startsAt) {
  if (!startsAt) return true;
  return new Date(startsAt).getTime() >= Date.now();
}

// ✅ normaliza {rows}, {items}, array puro...
function normalizeList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.rows)) return res.rows;
  if (Array.isArray(res?.items)) return res.items;
  return [];
}

export const dashboardThunks = {
  load: createAsyncThunk(
    "dashboard/load",
    async ({ role, userId }, { rejectWithValue }) => {
      try {
        const auctionsRes = await auctionsApi.list({
          q: "",
          limit: 9999,
          offset: 0,
        });
        const auctions = normalizeList(auctionsRes);
        const allAuctions = auctions;

        const futureAuctions = auctions.filter((a) =>
          isFutureOrNoDate(a?.starts_at),
        );

        let targetAuctions = [];

        if (role === "ADMIN") {
          targetAuctions = futureAuctions.filter(
            (a) => a?.research_status === "APPROVED",
          );
        } else {
          targetAuctions = futureAuctions.filter(
            (a) => a?.assigned_to_user_id === userId,
          );
        }

        const approvedUnpaidAuctions = allAuctions.filter(
          (a) =>
            a?.research_status === "APPROVED" && a?.payment_status !== "PAID",
        );

        const auctionsToLoadProps = [
          ...new Set([
            ...futureAuctions.map((a) => a.id), // ✅ pega todos futuros
            ...approvedUnpaidAuctions.map((a) => a.id),
          ]),
        ];

        const entries = await Promise.all(
          auctionsToLoadProps.map(async (auctionId) => {
            const propsRes = await auctionPropertiesApi.list({
              auction_id: auctionId,
              q: "",
              limit: 9999,
              offset: 0,
            });

            return [auctionId, normalizeList(propsRes)];
          }),
        );

        const propsByAuctionId = Object.fromEntries(entries);

        return {
          futureAuctions,
          approvedUnpaidAuctions,
          propsByAuctionId,
        };
      } catch (err) {
        return rejectWithValue(err?.message || "Erro ao carregar dashboard.");
      }
    },
  ),
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    futureAuctions: [],
    approvedUnpaidAuctions: [],
    propsByAuctionId: {},
    status: "idle",
    error: null,
    lastLoadedAt: null,
  },
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clear(state) {
      state.futureAuctions = [];
      state.approvedUnpaidAuctions = [];
      state.propsByAuctionId = {};
      state.status = "idle";
      state.error = null;
      state.lastLoadedAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(dashboardThunks.load.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(dashboardThunks.load.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.futureAuctions = action.payload.futureAuctions;
        state.approvedUnpaidAuctions = action.payload.approvedUnpaidAuctions;
        state.propsByAuctionId = action.payload.propsByAuctionId;
        state.lastLoadedAt = Date.now();
      })
      .addCase(dashboardThunks.load.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Erro ao carregar dashboard.";
      });
  },
});

export const dashboardActions = dashboardSlice.actions;
export default dashboardSlice.reducer;
